/**
 * Spouštění uživatelských programů: sandbox worker + timeout 800 ms
 * (nekonečný cyklus → worker se ukončí a vrátí se chyba, aplikace běží dál).
 * Fallback bez Workeru (testy): přímé spuštění s limitem velikosti vstupu.
 */
import { pragueParts } from './time';
import type { Itinerary } from './types';
import { programJs, type CustomProgram } from './programs';

export interface ProgramResult {
  ids: string[] | null;
  error: string | null;
}

/** Odlehčená kopie tras pro sandbox (bez geometrií — rychlý přenos). */
function lite(itins: Itinerary[]): Array<Record<string, unknown>> {
  return itins.map((t) => ({
    id: t.id,
    modeId: t.modeId,
    name: t.name,
    totalMin: t.totalMin,
    cost: t.cost,
    waitMin: t.waitMin,
    emissions: t.emissions,
    distanceKm: t.distanceKm,
    comfort: t.comfort,
    transfers: t.transfers,
    reliability: t.reliability,
    uncertaintyMin: t.uncertaintyMin,
    carFree: t.carFree,
    accessible: t.accessible,
    dataSource: t.dataSource,
    departureMs: t.departureMs,
    arrivalMs: t.arrivalMs,
    segments: t.segments.map((s) => ({ kind: s.kind, refId: s.refId, distanceKm: s.distanceKm })),
  }));
}

export function ctxOf(departMs: number): { hour: number; day: number; weekend: boolean } {
  const p = pragueParts(departMs);
  // 1 = pondělí … 7 = neděle (ISO)
  const day = ((new Date(departMs).getUTCDay() + 6) % 7) + 1;
  return { hour: p.hour, day, weekend: day >= 6 };
}

export function runProgram(
  program: CustomProgram,
  itins: Itinerary[],
  departMs: number,
  timeoutMs = 800,
): Promise<ProgramResult> {
  const compiled = programJs(program);
  if ('error' in compiled) return Promise.resolve({ ids: null, error: compiled.error });
  const payload = { jsSource: compiled.js, kind: program.kind, itins: lite(itins), ctx: ctxOf(departMs) };

  if (typeof Worker === 'undefined') {
    // Testovací/fallback prostředí: synchronně, bez ochrany proti smyčce.
    try {
      const fn = new Function(`return (${compiled.js});`)() as (...a: unknown[]) => unknown;
      let ids: string[];
      if (program.kind === 'filter') ids = payload.itins.filter((t) => Boolean(fn(t, payload.ctx))).map((t) => t.id as string);
      else if (program.kind === 'sort') ids = [...payload.itins].sort((a, b) => Number(fn(a, b, payload.ctx)) || 0).map((t) => t.id as string);
      else {
        const res = fn(payload.itins, payload.ctx);
        ids = Array.isArray(res) ? res.map((t) => (t as { id: string }).id) : [];
      }
      return Promise.resolve({ ids, error: null });
    } catch (e) {
      return Promise.resolve({ ids: null, error: e instanceof Error ? e.message : 'Program selhal.' });
    }
  }

  return new Promise((resolve) => {
    const worker = new Worker(new URL('../workers/programWorker.ts', import.meta.url), { type: 'module' });
    const timer = setTimeout(() => {
      worker.terminate();
      resolve({ ids: null, error: `Program běžel přes ${timeoutMs} ms (nekonečný cyklus?) — byl zastaven.` });
    }, timeoutMs);
    worker.onmessage = (e: MessageEvent<{ ok: boolean; ids?: string[]; error?: string }>) => {
      clearTimeout(timer);
      worker.terminate();
      resolve(e.data.ok ? { ids: e.data.ids ?? [], error: null } : { ids: null, error: e.data.error ?? 'Program selhal.' });
    };
    worker.onerror = () => {
      clearTimeout(timer);
      worker.terminate();
      resolve({ ids: null, error: 'Program se nepodařilo spustit.' });
    };
    worker.postMessage(payload);
  });
}

/** Dokumentace API pro JS režim (zobrazuje editor). */
export const JS_DOC = `JavaScript API (běží v sandboxu, timeout 800 ms):
FILTR:   function filtr(t, ctx) { return t.totalMin < 45; }
ŘAZENÍ:  function porovnej(a, b, ctx) { return (a.totalMin) - (b.totalMin); }
HLEDÁNÍ: function hledej(trasy, ctx) { return trasy.filter(t => t.cost < 60).sort((a,b) => a.totalMin - b.totalMin); }

Trasa t: totalMin, cost, waitMin, emissions, distanceKm, comfort (1–5),
transfers, reliability (0–1), uncertaintyMin, carFree, accessible,
dataSource ('real'|'mixed'|'estimate'), modeId, name, departureMs, arrivalMs,
segments [{kind, refId, distanceKm}].
Kontext ctx: hour (0–23), day (1=Po…7=Ne), weekend (true/false).`;
